import React, { useRef, useState } from 'react';
import { cn } from '@/src/infrastructure/lib/utils';
import { uploadMediaFile } from '@/src/infrastructure/lib/api';
import { Icons } from '@/src/modules/components/Icons';
import { trackEvent } from '@/src/infrastructure/lib/analytics';


const labelClass = 'ml-1 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant';

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime';

export interface MediaUploadFieldProps {
  id: string;
  label: string;
  value?: string;
  onChange: (url: string) => void;
  /** Which file kinds the picker offers. Defaults to images only. */
  accept?: 'image' | 'video' | 'both';
  /**
   * Server-side draft invitation id the upload is scoped to. When absent,
   * `ensureInvitationId` is called so the wizard can flush its autosave and
   * mint one — uploads are impossible without an invitation to own them.
   */
  invitationId?: string | null;
  ensureInvitationId?: () => Promise<string | null>;
  required?: boolean;
  error?: string;
  /**
   * Optional async transform run on the selected file before upload (e.g.
   * client-side background removal). Returning a `warning` shows it next to
   * the field without blocking the upload — the returned `file` is what
   * gets uploaded either way.
   */
  preprocess?: (file: File) => Promise<{ file: File; warning?: string }>;
}

/**
 * Upload-only media picker. The stored value is still the processed CDN URL,
 * but users never paste or edit that URL directly.
 */
export function MediaUploadField({
  id,
  label,
  value,
  onChange,
  accept = 'image',
  invitationId,
  ensureInvitationId,
  required,
  error,
  preprocess,
}: MediaUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<'idle' | 'processing' | 'uploading'>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [preprocessWarning, setPreprocessWarning] = useState<string | null>(null);
  const uploading = status !== 'idle';

  const acceptAttr =
    accept === 'both' ? `${IMAGE_ACCEPT},${VIDEO_ACCEPT}` : accept === 'video' ? VIDEO_ACCEPT : IMAGE_ACCEPT;

  const handleFile = async (file: File) => {
    setUploadError(null);
    setPreprocessWarning(null);
    setStatus('uploading');
    try {
      const invId = invitationId ?? (ensureInvitationId ? await ensureInvitationId() : null);
      if (!invId) {
        throw new Error('Save your draft first — uploads need an invitation to belong to.');
      }
      let fileToUpload = file;
      if (preprocess) {
        setStatus('processing');
        const processed = await preprocess(file);
        fileToUpload = processed.file;
        if (processed.warning) setPreprocessWarning(processed.warning);
      }
      setStatus('uploading');
      const result = await uploadMediaFile(invId, fileToUpload);
      trackEvent('MEDIA_UPLOADED', 'engagement');
      onChange(result.processedUrl);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setStatus('idle');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const shownError = error || uploadError || undefined;
  const uploadLabel = accept === 'video' ? 'Upload Video' : accept === 'both' ? 'Upload Media' : 'Upload Photo';
  const previewAlt = value ? `${label} preview` : undefined;

  return (
    <div className="flex flex-col gap-2">
      <span className={cn(labelClass, shownError && 'text-status-failed')}>
        {label}{required && <span className="ml-1 text-status-failed">*</span>}
      </span>
      <div
        className={cn(
          'overflow-hidden rounded-2xl border border-outline-variant bg-surface-lowest',
          shownError && 'border-status-failed bg-status-failed/5 ring-4 ring-status-failed/10',
        )}
      >
        <div className="flex min-h-36 items-center justify-center bg-surface-low">
          {value ? (
            accept === 'video' ? (
              <video src={value} controls className="max-h-56 w-full object-contain" aria-label={previewAlt} />
            ) : (
              <img src={value} alt={previewAlt} className="max-h-56 w-full object-cover" />
            )
          ) : (
            <div className="flex flex-col items-center gap-2 px-4 py-8 text-center text-on-surface-variant">
              <Icons.Camera size={28} />
              <p className="text-sm font-semibold">No photo selected</p>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-outline-variant/20 px-4 py-3">
          <p className="min-w-0 flex-1 text-xs text-on-surface-variant">
            {value ? 'Preview ready' : 'Choose a photo from your device'}
          </p>
          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              disabled={uploading}
              className="rounded-xl px-3 py-2 text-xs font-semibold text-on-surface-variant transition-colors hover:bg-surface-low hover:text-on-surface disabled:cursor-not-allowed disabled:opacity-60"
            >
              Remove
            </button>
          )}
          <button
            id={id}
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className={cn(
              'inline-flex shrink-0 items-center gap-2 rounded-xl border border-outline-variant bg-surface px-4 py-2 text-sm font-semibold text-on-surface transition-all hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60',
            )}
            aria-invalid={Boolean(shownError)}
            aria-describedby={shownError ? `${id}-error` : undefined}
            aria-label={`${uploadLabel} for ${label}`}
          >
            <Icons.Upload size={16} />
            {status === 'processing' ? 'Removing background...' : status === 'uploading' ? 'Uploading...' : uploadLabel}
          </button>
        </div>
        <button
          type="button"
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptAttr}
        className="hidden"
        data-testid={`${id}-file-input`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
      {shownError && (
        <p id={`${id}-error`} className="ml-1 text-xs font-semibold text-status-failed">{shownError}</p>
      )}
      {!shownError && preprocessWarning && (
        <p className="ml-1 text-xs font-semibold text-amber-600">{preprocessWarning}</p>
      )}
    </div>
  );
}
